import dayjs from "dayjs";
import type {
  PelletsChartDataState,
  PelletsChartPoint,
  PelletsHistoryChartPayload,
} from "~/types/Charts";

export const DEFAULT_PELLETS_CHART_MONTHS = 3;

const ALLOWED_PELLETS_CHART_MONTHS = [3, 6, 12] as const;

export type PelletsChartMonths = typeof ALLOWED_PELLETS_CHART_MONTHS[number];

export const COUNTY_PAGE_DEFAULT_MONTHS: PelletsChartMonths = 3;

function isAllowedMonths(value: unknown): value is PelletsChartMonths {
  return (ALLOWED_PELLETS_CHART_MONTHS as readonly number[]).includes(
    value as number,
  );
}

export function coercePelletsChartMonths(value: unknown): PelletsChartMonths {
  if (isAllowedMonths(value)) return value;
  if (process.env.NODE_ENV === "development" && value !== undefined) {
    console.warn(
      `[pelletsChart] Unsupported months value: ${String(value)}. ` +
        `Allowed: ${ALLOWED_PELLETS_CHART_MONTHS.join(", ")}.`,
    );
  }
  return DEFAULT_PELLETS_CHART_MONTHS;
}

export function rowsToChartPoints(
  rows: [number, number][] | null | undefined,
): PelletsChartPoint[] {
  if (!Array.isArray(rows)) return [];
  const points: PelletsChartPoint[] = [];
  for (const row of rows) {
    if (row[0] != null && row[1] != null) {
      points.push({ x: row[0], y: row[1] });
    }
  }
  return points;
}

export function pointsToDateLabels(points: PelletsChartPoint[]): string[] {
  return points.map((p) => dayjs.unix(p.x).format("DD.MM.YYYY"));
}

export function clonePelletsChartState(
  state: PelletsChartDataState,
): PelletsChartDataState {
  return {
    labels: [...state.labels],
    loose: { data: state.loose.data.map((p) => ({ ...p })) },
    pallet: { data: state.pallet.data.map((p) => ({ ...p })) },
  };
}

export function buildAlignedPelletsChartState(
  payload: PelletsHistoryChartPayload | null | undefined,
): PelletsChartDataState {
  const looseByX = new Map<number, number>(
    rowsToChartPoints(payload?.loose).map((p) => [p.x, p.y as number]),
  );
  const palletByX = new Map<number, number>(
    rowsToChartPoints(payload?.pallet).map((p) => [p.x, p.y as number]),
  );

  const xs = Array.from(
    new Set<number>([...looseByX.keys(), ...palletByX.keys()]),
  ).sort((a, b) => a - b);

  return {
    labels: xs.map((x) => dayjs.unix(x).format("DD.MM.YYYY")),
    loose: { data: xs.map((x) => ({ x, y: looseByX.get(x) ?? null })) },
    pallet: { data: xs.map((x) => ({ x, y: palletByX.get(x) ?? null })) },
  };
}
